import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { displayNotification, handleRequest } from '../../../../helpers';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthState } from '../../../../redux/authSlice';
import { setHeaderTheme } from '../../../../redux/generalSlice';
import Redirect from '../../../components/Redirect';
import { useTranslation } from "react-i18next";
import { BASE_URL } from '../../../../constants';
import { Mail, Lock, ShieldCheck, Eye, EyeOff, LogIn } from 'lucide-react';

const Login = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = useSelector(s => s.auth.data.id);

    const [isWorking, setIsWorking] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const authData = useSelector(s => s.auth.data);

    useEffect(() => {
        dispatch(setHeaderTheme('white'));
    }, [dispatch]);

    useEffect(() => {
        if (authData && authData.id) {
            navigate('/portal');
        }
    }, [authData, navigate]);

    const getAuthRoute = () => {
        return searchParams.get('return') ? searchParams.get('return') : '/portal';
    };

    const handleLogin = async () => {
        try {
            if (email.trim() === '') {
                displayNotification('warning', t("886", "Please fill email field."));
                return false;
            }
            if (password === '') {
                displayNotification('warning', t("887", "Please fill password field."));
                return false;
            }
            setIsWorking(true);
            const res = await handleRequest(`/users/login`, 'POST', {
                email: email.trim(),
                password: password
            });
            if (res.success && res.status === 200) {
                dispatch(setAuthState({ ...res.data, accessToken: res.accessToken }));
                displayNotification('success', t("888", "Login Successful"));
                navigate(getAuthRoute());
            } else {
                displayNotification('error', t("888", "Invalid Credentials"));
            }
            setIsWorking(false);
        } catch (e) {
            setIsWorking(false);
            console.error(e);
            displayNotification('error', `${t("889", "Error logging in")} [F]`);
        }
    };

    return userId === '' ? (
        <div className="App" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#F8FAFC' }}>
            <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px' }}>
                <div style={{ width: '100%', maxWidth: '440px' }}>
                    {/* Header Brand Badge */}
                    <div className="text-center mb-4">
                        <div
                            className="d-inline-flex align-items-center justify-content-center p-3 rounded-4 bg-white shadow-sm mb-3"
                            style={{ border: '1px solid #E2E8F0' }}
                        >
                            <img
                                src={`${BASE_URL}/assets/icons/logo.jpg`}
                                height="48"
                                alt="eRespiroConnect"
                                style={{ objectFit: 'contain' }}
                            />
                        </div>
                        <h2 className="fw-bold mb-1" style={{ fontSize: '1.5rem', letterSpacing: '-0.02em', color: '#0F172A' }}>
                            eRespiroConnect
                        </h2>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                            <span className="badge rounded-pill px-3 py-1" style={{ backgroundColor: '#FEE2E2', color: '#B11116', fontWeight: 600, fontSize: '0.75rem' }}>
                                Central Data Portal
                            </span>
                            <span className="text-muted small">• TB REACH Wave 11</span>
                        </div>
                    </div>

                    {/* Authentication Card */}
                    <div className="card shadow-lg border-0 rounded-4 overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0' }}>
                        <div
                            className="px-4 py-3"
                            style={{
                                background: 'linear-gradient(135deg, #740203 0%, #8B0304 60%, #B11116 100%)',
                                color: '#FFFFFF'
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="fw-bold text-white mb-0" style={{ fontSize: '1.05rem' }}>
                                        Portal Sign In
                                    </h4>
                                    <span style={{ fontSize: '0.78rem', opacity: 0.85 }}>
                                        Enter your credentials to access surveillance data
                                    </span>
                                </div>
                                <div className="p-2 rounded-circle" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                                    <ShieldCheck size={20} color="#FFFFFF" />
                                </div>
                            </div>
                        </div>

                        <div className="card-body p-4">
                            <div className="mb-3">
                                <label className="form-label small fw-semibold text-secondary mb-1">
                                    Email Address
                                </label>
                                <div className="portal-input-group">
                                    <span className="portal-input-icon me-2">
                                        <Mail size={18} color="#64748B" />
                                    </span>
                                    <input
                                        type="email"
                                        className="portal-input-field"
                                        placeholder="name@dopasi.org"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleLogin()}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <label className="form-label small fw-semibold text-secondary mb-0">
                                        Password
                                    </label>
                                </div>
                                <div className="portal-input-group">
                                    <span className="portal-input-icon me-2">
                                        <Lock size={18} color="#64748B" />
                                    </span>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="portal-input-field"
                                        placeholder="••••••••••••"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleLogin()}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link p-0 text-muted border-0 ms-2"
                                        style={{ boxShadow: 'none', background: 'transparent' }}
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex={-1}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? <EyeOff size={18} color="#64748B" /> : <Eye size={18} color="#64748B" />}
                                    </button>
                                </div>
                            </div>

                            <button
                                className="btn btn-primary w-100 py-2 d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                onClick={handleLogin}
                                disabled={isWorking}
                                style={{
                                    background: 'linear-gradient(135deg, #B11116 0%, #8B0304 100%)',
                                    borderRadius: 10,
                                    fontWeight: 700
                                }}
                            >
                                {isWorking ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                        <span>{t("901", "Verifying...")}</span>
                                    </>
                                ) : (
                                    <>
                                        <LogIn size={18} />
                                        <span>{t("714", "Sign In to Portal")}</span>
                                    </>
                                )}
                            </button>

                            <div className="text-center mt-3 pt-3 border-top" style={{ borderColor: '#F1F5F9' }}>
                                <p className="text-muted small mb-0">
                                    Authorized Health & Epidemiological Personnel Only
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Security Notice */}
                    <div className="text-center mt-3">
                        <span className="text-muted small">
                            Protected by Dopasi Foundation Data Security Protocols
                        </span>
                    </div>
                </div>
            </main>
        </div>
    ) : (
        <Redirect to={getAuthRoute()} />
    );
};

export default Login;
